import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { customerApi } from '../services/customerApi';

const CustomerAuthContext = createContext(null);

export const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [customerToken, setCustomerToken] = useState(localStorage.getItem('customer_token'));
  const [loading, setLoading] = useState(true);

  // Extract customer object from API response
  const extractCustomer = (res) => {
    if (!res) return null;
    return res.customer || res.user || res.data?.customer || res.data?.user || res.data || res;
  };

  // Extract token from API response
  const extractToken = (res) => {
    if (!res) return null;
    return res.token || res.access_token || res.session_token || res.data?.token || res.data?.access_token;
  };

  const fetchCurrentCustomer = useCallback(async (tokenToUse) => {
    const activeToken = tokenToUse || customerToken;
    if (!activeToken) {
      setCustomer(null);
      setLoading(false);
      return null;
    }

    try {
      const res = await customerApi.getCurrentCustomer(activeToken);
      const cust = extractCustomer(res);
      setCustomer(cust);
      return cust;
    } catch (error) {
      console.error('Customer session validation failed:', error.message);
      // Remove only customer_token on invalid/expired session
      localStorage.removeItem('customer_token');
      setCustomerToken(null);
      setCustomer(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [customerToken]);

  useEffect(() => {
    fetchCurrentCustomer();
  }, [fetchCurrentCustomer]);

  const login = async (mobile, password) => {
    const res = await customerApi.login({ mobile, password });
    const token = extractToken(res);
    
    if (token) {
      localStorage.setItem('customer_token', token);
      setCustomerToken(token);
      const cust = await fetchCurrentCustomer(token);
      return cust || extractCustomer(res);
    } else {
      const cust = extractCustomer(res);
      setCustomer(cust);
      return cust;
    }
  };

  const signup = async (name, mobile, password) => {
    const res = await customerApi.signup({ name, mobile, password });
    const token = extractToken(res);

    if (token) {
      localStorage.setItem('customer_token', token);
      setCustomerToken(token);
      const cust = await fetchCurrentCustomer(token);
      return cust || extractCustomer(res);
    } else {
      const cust = extractCustomer(res);
      setCustomer(cust);
      return cust;
    }
  };

  const logout = () => {
    localStorage.removeItem('customer_token');
    setCustomerToken(null);
    setCustomer(null);
  };

  const deleteAccount = async (password) => {
    if (!customerToken) {
      throw new Error('Not authenticated.');
    }
    const res = await customerApi.deleteAccount(customerToken, password);
    logout();
    return res;
  };

  return (
    <CustomerAuthContext.Provider
      value={{
        customer,
        customerToken,
        loading,
        login,
        signup,
        logout,
        deleteAccount,
        refreshCustomer: () => fetchCurrentCustomer(customerToken)
      }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
};

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within CustomerAuthProvider');
  }
  return context;
};
